import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { useI18n, withLang } from "@/i18n/I18nProvider";
import { Mail, MapPin, Building2 } from "lucide-react";

export function Footer() {
  const { t, lang } = useI18n();

  const HASH_LINKS = [
    { key: "how", hash: "#como-funciona" },
    { key: "projects", hash: "#proyectos" },
    { key: "stats", hash: "#cifras" },
    { key: "contact", hash: "#contacto" },
  ];

  const handleAnchor = (e, hash) => {
    e.preventDefault();
    const el = document.querySelector(hash);
    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="relative bg-[#0B132B] border-t border-slate-800/70" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Logo size={38} />
            <p className="mt-6 text-sm text-slate-400 max-w-md leading-relaxed">
              {t("footer.tagline")}
            </p>
            <div className="mt-6 flex flex-col gap-2 text-xs text-slate-400 font-mono">
              <span className="inline-flex items-center gap-2"><Building2 className="w-3.5 h-3.5 text-emerald-400" /> {t("footer.company_name")} · {t("footer.cuit")}</span>
              <span className="inline-flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-emerald-400" /> {t("footer.address")}</span>
              <a href="mailto:hola@zantravika.com" className="inline-flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-email-link">
                <Mail className="w-3.5 h-3.5 text-emerald-400" /> hola@zantravika.com
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="eyebrow mb-4">{t("footer.nav_title")}</p>
            <ul className="space-y-3 text-sm text-slate-300">
              {HASH_LINKS.map((l) => (
                <li key={l.key}>
                  <a href={l.hash} onClick={(e) => handleAnchor(e, l.hash)} className="hover:text-emerald-400 transition-colors" data-testid={`footer-nav-${l.key}-link`}>
                    {t(`nav.${l.key}`)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="eyebrow mb-4">{t("footer.legal_title")}</p>
            <ul className="space-y-3 text-sm text-slate-300">
              <li><Link to={withLang(lang, "/privacy")} className="hover:text-emerald-400 transition-colors" data-testid="footer-privacy-link">{t("footer.privacy")}</Link></li>
              <li><Link to={withLang(lang, "/cookies")} className="hover:text-emerald-400 transition-colors" data-testid="footer-cookies-link">{t("footer.cookies")}</Link></li>
              <li><Link to={withLang(lang, "/terms")} className="hover:text-emerald-400 transition-colors" data-testid="footer-terms-link">{t("footer.terms")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="eyebrow mb-4">{t("footer.company_title")}</p>
            <ul className="space-y-3 text-xs text-slate-400 font-mono">
              <li>{t("footer.igj")}</li>
              <li>{t("footer.jurisdiction")}</li>
              <li>{t("footer.year")}</li>
            </ul>
          </div>
        </div>

        <div className="hairline my-10" />

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs text-slate-500 font-mono">© {new Date().getFullYear()} Zantravika S.A. — {t("footer.rights")}</p>
          <p className="text-[11px] text-slate-500 max-w-xl leading-relaxed">
            {t("footer.risk")}
          </p>
        </div>

        <p className="mt-6 text-[11px] text-slate-600 max-w-3xl leading-relaxed">
          {t("footer.license")}
        </p>
      </div>
    </footer>
  );
}

export default Footer;
